import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, matchPath } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { Toaster } from './components/ui/sonner';
import { Navbar } from './components/Navbar';
import { CategoryNavbar } from './components/CategoryNavbar';
import { Footer } from './components/Footer';
import { CookieConsent } from './components/CookieConsent';
import GoogleAnalytics from './components/GoogleAnalytics';
import ShopApp from './shop/ShopApp';
import { getShopSession, exitShop, isNeutralPath, markMarketplaceSeen, remapToShop, shortLinkPath } from './lib/shopSession';
import LaunchBanner from './components/LaunchBanner';
import { supabase } from './lib/supabase';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import OAuthCallbackPage from './pages/OAuthCallbackPage';
import EmailConfirmedPage from './pages/EmailConfirmedPage';
import CheckEmailPage from './pages/CheckEmailPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import UnifiedDashboard from './pages/UnifiedDashboardNew';
import CraftersPage from './pages/CraftersPage';
import ProductsPage from './pages/ProductsPage';
import RequestsPage from './pages/RequestsPage';
import CrafterProfilePage from './pages/CrafterProfilePage';
import ProductDetailPage from './pages/ProductDetailPage';
import RequestDetailPage from './pages/RequestDetailPage';
import MessagesPage from './pages/MessagesPage';
import ProfilePage from './pages/ProfilePage';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import { AdminTwoFactorGate } from './components/AdminTwoFactorGate';
import { RequireAdmin } from './components/RequireAdmin';
import { AccountDisabledGate } from './components/AccountDisabledGate';
import AdminShipmentsPage from './pages/AdminShipmentsPage';
import AdminOrderCasesPage from './pages/AdminOrderCasesPage';
import OrderCasePage from './pages/OrderCasePage';
import PlansPage from './pages/PlansPage';
import MaintenancePage from './pages/MaintenancePage';
import CartPage from './pages/CartPage';
import FavoritesPage from './pages/FavoritesPage';
import CategoryLandingPage from './pages/CategoryLandingPage';
import CategoriesIndexPage from './pages/CategoriesIndexPage';
import GiftLandingPage from './pages/GiftLandingPage';
import HowItWorksPage from './pages/HowItWorksPage';
import HowToBuyPage from './pages/HowToBuyPage';
import CustomProductPage from './pages/CustomProductPage';
import PaymentsPage from './pages/PaymentsPage';
import ShippingPage from './pages/ShippingPage';
import ReturnsPage from './pages/ReturnsPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import DisputesPage from './pages/DisputesPage';
import ManifestoPage from './pages/ManifestoPage';
import HelpCenterPage from './pages/HelpCenterPage';
import FAQPage from './pages/FAQPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import HowToSellPage from './pages/HowToSellPage';
import CommissioniPage from './pages/CommissioniPage';
import LineeGuidaPage from './pages/LineeGuidaPage';
import CosplayRequestPage from './pages/CosplayRequestPage';
import MatrimonioRequestPage from './pages/MatrimonioRequestPage';
import GioielliRequestPage from './pages/GioielliRequestPage';
import ArredamentoRequestPage from './pages/ArredamentoRequestPage';
import PitturaRequestPage from './pages/PitturaRequestPage';
import SculturaRequestPage from './pages/SculturaRequestPage';
import ArteRequestPage from './pages/ArteRequestPage';
import PortachiaviRequestPage from './pages/PortachiaviRequestPage';
import CamicieRequestPage from './pages/CamicieRequestPage';
import PortafogliRequestPage from './pages/PortafogliRequestPage';
import CintureRequestPage from './pages/CintureRequestPage';
import PelletteriaRequestPage from './pages/PelletteriaRequestPage';
import CeramicheRequestPage from './pages/CeramicheRequestPage';
import VetroRequestPage from './pages/VetroRequestPage';
import FesteRequestPage from './pages/FesteRequestPage';
import BattesimiRequestPage from './pages/BattesimiRequestPage';
import ComunioniRequestPage from './pages/ComunioniRequestPage';
import LaureeRequestPage from './pages/LaureeRequestPage';
import TaglieriRequestPage from './pages/TaglieriRequestPage';
import CorniciRequestPage from './pages/CorniciRequestPage';
import ScultureIntagliRequestPage from './pages/ScultureIntagliRequestPage';
import OggettiDecorativiRequestPage from './pages/OggettiDecorativiRequestPage';
import CofanettiScatoleRequestPage from './pages/CofanettiScatoleRequestPage';
import Stampa3DRequestPage from './pages/Stampa3DRequestPage';
import FalegnameriaRequestPage from './pages/FalegnameriaRequestPage';
import BusinessRequestPage from './pages/BusinessRequestPage';
import TessileRequestPage from './pages/TessileRequestPage';
import ExplorePage from './pages/ExplorePage';
import './App.css';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

function App() {
  const [maintenanceBypassed, setMaintenanceBypassed] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(true);

  useEffect(() => {
    // Check if user has already bypassed maintenance
    const bypassed = localStorage.getItem('rarifex_maintenance_bypass') === 'true';
    setMaintenanceBypassed(bypassed);
    
    // Load maintenance mode from Supabase directly (works in production)
    const loadMaintenanceMode = async () => {
      try {
        // Try backend first (works in preview)
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        let maintenanceEnabled = false;
        
        try {
          const response = await fetch(`${backendUrl}/api/settings/maintenance`);
          if (response.ok) {
            const data = await response.json();
            maintenanceEnabled = data.maintenance_mode === true;
          } else {
            throw new Error('Backend not available');
          }
        } catch (backendError) {
          // Fallback: read directly from Supabase (for production deploy)
          console.log('Backend not available, reading from Supabase directly');
          const { data, error } = await supabase
            .from('site_settings')
            .select('value')
            .eq('key', 'maintenance_mode')
            .single();
          
          if (!error && data) {
            maintenanceEnabled = data.value === true;
          }
        }
        
        setMaintenanceMode(maintenanceEnabled);
      } catch (error) {
        console.error('Error loading maintenance mode:', error);
        setMaintenanceMode(false);
      } finally {
        setMaintenanceLoading(false);
      }
    };
    
    loadMaintenanceMode();
    
    // Listen for maintenance mode changes from admin dashboard (real-time update)
    const handleMaintenanceChange = (event) => {
      setMaintenanceMode(event.detail.enabled);
      // If maintenance is disabled, no need to bypass anymore
      if (!event.detail.enabled) {
        localStorage.removeItem('rarifex_maintenance_bypass');
      }
    };
    
    window.addEventListener('maintenanceModeChanged', handleMaintenanceChange);
    return () => window.removeEventListener('maintenanceModeChanged', handleMaintenanceChange);
  }, []);

  // Show loading while checking maintenance mode
  if (maintenanceLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008080]"></div>
      </div>
    );
  }

  // Show maintenance page if enabled and not bypassed
  if (maintenanceMode && !maintenanceBypassed) {
    return <MaintenancePage onBypass={() => setMaintenanceBypassed(true)} />;
  }

  return (
    <HelmetProvider>
      <AuthProvider>
        <LanguageProvider>
          <NotificationProvider>
            <Router>
              <AppContent maintenanceMode={maintenanceMode} maintenanceBypassed={maintenanceBypassed} setMaintenanceBypassed={setMaintenanceBypassed} />
            </Router>
          </NotificationProvider>
        </LanguageProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

// Separate component to use useLocation inside Router
function AppContent({ maintenanceMode, maintenanceBypassed, setMaintenanceBypassed }) {
  const location = useLocation();
  
  // Hide footer on dashboard pages
  const isDashboardPage = location.pathname.includes('/dashboard');

  // MODALITÀ NEGOZIO: /artigiano/<slug>/... (pagine del negozio), oppure la vetrina
  // aperta dal link della bottega (?ref), da "Entra nel negozio" (?negozio=1)
  // o con il negozio già aperto in questa sessione.
  const shopMatch = matchPath('/artigiano/:shopSlug/*', location.pathname);
  const shopSlug = shopMatch?.params?.shopSlug;
  const search = new URLSearchParams(location.search);
  const inShop = !!shopSlug && (
    !!shopMatch.params['*'] ||
    (search.get('ref') || '').toLowerCase() === shopSlug.toLowerCase() ||
    search.get('negozio') === '1' ||
    getShopSession() === shopSlug
  );

  // Con il negozio aperto: carrello, accesso, ordine, messaggi e pagine informative
  // si vedono dentro il negozio; i rimandi al marketplace (home, catalogo, scheda
  // prodotto) portano alla pagina equivalente della bottega. Un caricamento diretto
  // (es. l'utente digita rarifex.com) non viene rimappato: si torna al marketplace.
  const shopSession = !inShop ? getShopSession() : null;
  const isInitialLoad = location.key === 'default';
  const embeddedInShop = !!shopSession && isNeutralPath(location.pathname);
  const remapTarget = shopSession && !isInitialLoad && !embeddedInShop ? remapToShop(location.pathname, shopSession) : null;

  // Navigare il marketplace fa uscire dal negozio e conta come "primo contatto" con Rarifex
  useEffect(() => {
    if (inShop || remapTarget || isNeutralPath(location.pathname) || shortLinkPath(location.pathname)) return;
    exitShop();
    markMarketplaceSeen();
  }, [inShop, remapTarget, location.pathname]);
  
  // Show maintenance page if enabled and not bypassed
  if (maintenanceMode && !maintenanceBypassed) {
    return <MaintenancePage onBypass={() => setMaintenanceBypassed(true)} />;
  }

  // Link breve /@<slug> (di norma lo gestisce middleware.js su Vercel)
  const shortTarget = shortLinkPath(location.pathname, location.search);
  if (shortTarget) {
    return <Navigate to={shortTarget} replace />;
  }

  if (remapTarget) {
    return <Navigate to={remapTarget} replace />;
  }

  if (inShop || embeddedInShop) {
    return (
      <AccountDisabledGate>
        <ScrollToTop />
        {/* Rotta padre: le pagine del negozio sono rotte relative a /artigiano/:shopSlug;
            le pagine Rarifex incorporate restano al loro indirizzo, con la grafica della bottega */}
        <Routes>
          <Route path="/artigiano/:shopSlug/*" element={<ShopApp slug={shopSlug} />} />
          <Route path="*" element={<ShopApp slug={shopSession} embedded />} />
        </Routes>
        <GoogleAnalytics />
        <CookieConsent />
        <Toaster position="top-center" />
      </AccountDisabledGate>
    );
  }

  return (
    <AccountDisabledGate>
    <>
      <ScrollToTop />
      <div className="flex flex-col min-h-screen">
        <LaunchBanner />
        <Navbar />
        <CategoryNavbar />
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/auth/callback/:provider" element={<OAuthCallbackPage />} />
            <Route path="/auth/email-confirmed" element={<EmailConfirmedPage />} />
            <Route path="/auth/check-email" element={<CheckEmailPage />} />
            <Route path="/order-success" element={<OrderSuccessPage />} />
                    <Route path="/dashboard" element={<UnifiedDashboard />} />
                    <Route path="/client/dashboard" element={<UnifiedDashboard />} />
                    <Route path="/crafter/dashboard" element={<UnifiedDashboard />} />
                    <Route path="/esplora" element={<ExplorePage />} />
                    <Route path="/products" element={<ProductsPage />} />
                    <Route path="/crafters" element={<CraftersPage />} />
                    {/* Category landing pages */}
                    <Route path="/categoria/:category" element={<CategoryLandingPage />} />
                    <Route path="/categoria/:category/:subcategory" element={<CategoryLandingPage />} />
                    <Route path="/categoria/:category/:subcategory/:subsubcategory" element={<CategoryLandingPage />} />
                    <Route path="/categoria/:category/:subcategory/:subsubcategory/:subsubsubcategory" element={<CategoryLandingPage />} />
                    {/* Indice categorie */}
                    <Route path="/categorie" element={<CategoriesIndexPage />} />
                    <Route path="/categories" element={<Navigate to="/categorie" replace />} />
                    <Route path="/requests" element={<RequestsPage />} />
                    <Route path="/crafter/:id" element={<CrafterProfilePage />} />
                    <Route path="/product/:id" element={<ProductDetailPage />} />
                    <Route path="/artigiano/:slug" element={<CrafterProfilePage />} />
                    <Route path="/prodotto/:slug" element={<ProductDetailPage />} />
                    <Route path="/request/:id" element={<RequestDetailPage />} />
                    <Route path="/messages" element={<MessagesPage />} />
                    <Route path="/messages/:conversationId" element={<MessagesPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/plans" element={<PlansPage />} />
                    <Route path="/diventa-artigiano" element={<PlansPage />} />
                    <Route path="/cart" element={<CartPage />} />
                    <Route path="/favorites" element={<FavoritesPage />} />
                    <Route path="/regalo-personalizzato" element={<GiftLandingPage />} />
                    <Route path="/come-funziona" element={<HowItWorksPage />} />
                    <Route path="/come-acquistare" element={<HowToBuyPage />} />
                    <Route path="/prodotto-su-misura" element={<CustomProductPage />} />
                    <Route path="/pagamenti" element={<PaymentsPage />} />
                    <Route path="/spedizioni" element={<ShippingPage />} />
                    <Route path="/resi-rimborsi" element={<ReturnsPage />} />
                    <Route path="/chi-siamo" element={<AboutPage />} />
                    <Route path="/contatti" element={<ContactPage />} />
                    <Route path="/controversie" element={<DisputesPage />} />
                    <Route path="/manifesto" element={<ManifestoPage />} />
                    <Route path="/assistenza" element={<HelpCenterPage />} />
                    <Route path="/faq" element={<FAQPage />} />
                    <Route path="/privacy" element={<PrivacyPolicyPage />} />
                    <Route path="/termini" element={<TermsPage />} />
                    <Route path="/cookie" element={<CookiePolicyPage />} />
                    <Route path="/come-vendere" element={<HowToSellPage />} />
                    <Route path="/commissioni" element={<CommissioniPage />} />
                    <Route path="/linee-guida" element={<LineeGuidaPage />} />
                    <Route path="/cosplay-su-misura" element={<CosplayRequestPage />} />
                    <Route path="/matrimonio-artigianale" element={<MatrimonioRequestPage />} />
                    <Route path="/gioielli-su-misura" element={<GioielliRequestPage />} />
                    <Route path="/arredamento-su-misura" element={<ArredamentoRequestPage />} />
                    <Route path="/dipinti-su-commissione" element={<PitturaRequestPage />} />
                    <Route path="/sculture-su-commissione" element={<SculturaRequestPage />} />
                    <Route path="/arte-su-commissione" element={<ArteRequestPage />} />
                    <Route path="/portachiavi-personalizzati" element={<PortachiaviRequestPage />} />
                    <Route path="/camicie-su-misura" element={<CamicieRequestPage />} />
                    <Route path="/portafogli-su-misura" element={<PortafogliRequestPage />} />
                    <Route path="/cinture-su-misura" element={<CintureRequestPage />} />
                    <Route path="/pelletteria-su-misura" element={<PelletteriaRequestPage />} />
                    <Route path="/ceramiche-su-misura" element={<CeramicheRequestPage />} />
                    <Route path="/vetro-su-misura" element={<VetroRequestPage />} />
                    <Route path="/feste-su-misura" element={<FesteRequestPage />} />
                    <Route path="/battesimi-su-misura" element={<BattesimiRequestPage />} />
                    <Route path="/comunioni-su-misura" element={<ComunioniRequestPage />} />
                    <Route path="/lauree-su-misura" element={<LaureeRequestPage />} />
                    <Route path="/taglieri-su-misura" element={<TaglieriRequestPage />} />
                    <Route path="/cornici-su-misura" element={<CorniciRequestPage />} />
                    <Route path="/sculture-intagli-su-misura" element={<ScultureIntagliRequestPage />} />
                    <Route path="/oggetti-decorativi-su-misura" element={<OggettiDecorativiRequestPage />} />
                    <Route path="/cofanetti-scatole-su-misura" element={<CofanettiScatoleRequestPage />} />
                    <Route path="/stampa-3d-su-misura" element={<Stampa3DRequestPage />} />
                    <Route path="/falegnameria-su-misura" element={<FalegnameriaRequestPage />} />
                    <Route path="/business-su-misura" element={<BusinessRequestPage />} />
                    <Route path="/tessile-su-misura" element={<TessileRequestPage />} />
                    <Route path="/admin" element={<RequireAdmin><AdminTwoFactorGate><SuperAdminDashboard /></AdminTwoFactorGate></RequireAdmin>} />
                    <Route path="/admin/shipments" element={<RequireAdmin><AdminTwoFactorGate><AdminShipmentsPage /></AdminTwoFactorGate></RequireAdmin>} />
                    <Route path="/admin/order-cases" element={<RequireAdmin><AdminTwoFactorGate><AdminOrderCasesPage /></AdminTwoFactorGate></RequireAdmin>} />
                    <Route path="/assistenza-ordine/:caseId" element={<OrderCasePage />} />
                  </Routes>
                </main>
                {!isDashboardPage && <Footer />}
                <GoogleAnalytics />
                <CookieConsent />
                <Toaster position="top-center" />
              </div>
            </>
            </AccountDisabledGate>
          );
        }

export default App;

